import React, { useState, useEffect } from 'react'
import { 
  IonContent, 
  IonPage, 
  IonButton, 
  IonInput, 
  IonItem, 
  IonLabel,
  IonToast,
  IonText,
  IonIcon,
  IonAlert
} from '@ionic/react'
import { useHistory } from 'react-router-dom'
import { arrowBackOutline, eyeOutline, eyeOffOutline } from 'ionicons/icons'
import { useTranslation } from 'react-i18next'
import { supabase } from '../../services/supabase'
import { useAuthStore } from '../../store/authStore'
import LanguageSwitcher from '../../components/LanguageSwitcher'

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastColor, setToastColor] = useState<'danger' | 'success'>('danger')
  const [showForgotPasswordAlert, setShowForgotPasswordAlert] = useState(false)
  const [resetEmail, setResetEmail] = useState('')
  const [resetLoading, setResetLoading] = useState(false)
  
  const history = useHistory()
  const { t } = useTranslation()
  const { user } = useAuthStore()

  // Redirect if user is already authenticated
  useEffect(() => {
    if (user) {
      console.log('LoginPage: User authenticated, redirecting to dashboard')
      if (user.role === 'admin') {
        history.replace('/app/admin')
      } else {
        history.replace('/app/dashboard')
      }
    }
  }, [user, history])

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      })

      if (error) throw error

      if (data.user) {
        // Let App.tsx and AuthGuard handle all routing
        // Don't redirect here - let the auth system handle it
        console.log('Login successful, letting auth system handle routing...')
        // The auth state change will trigger, user will be set, and routing will happen automatically
      }
    } catch (error: any) {
      console.error('Login error:', error)
      let errorMessage = t('auth.login.errors.generic')
      
      if (error.message.includes('Invalid login credentials')) {
        errorMessage = t('auth.login.errors.invalidCredentials')
      } else if (error.message.includes('Email not confirmed')) {
        errorMessage = t('auth.login.errors.emailNotConfirmed')
      } else if (error.message.includes('Too many requests')) {
        errorMessage = t('auth.login.errors.tooManyRequests')
      } else {
        errorMessage = error.message
      }
      
      setToastMessage(errorMessage)
      setToastColor('danger')
      setShowToast(true)
    } finally {
      setLoading(false)
    }
  }

  const handleForgotPassword = async () => {
    if (!resetEmail.trim()) {
      setToastMessage(t('auth.resetPassword.forgotPassword.enterEmail'))
      setToastColor('danger')
      setShowToast(true)
      return
    }

    setResetLoading(true)
    
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail.trim(), {
        redirectTo: `${window.location.origin}/reset-password`,
      })

      if (error) throw error

      setToastMessage(t('auth.resetPassword.forgotPassword.success'))
      setToastColor('success')
      setShowToast(true)
      setShowForgotPasswordAlert(false)
      setResetEmail('')
    } catch (error: any) {
      console.error('Password reset error:', error)
      setToastMessage(error.message || t('auth.resetPassword.forgotPassword.error'))
      setToastColor('danger')
      setShowToast(true)
    } finally {
      setResetLoading(false)
    }
  }

  return (
    <IonPage>
      <IonContent className="ion-padding">
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-500 to-primary-700">
          <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
            {/* Header with Back Button and Language Switcher */}
            <div className="mb-4 flex justify-between items-center">
              <IonButton 
                fill="clear" 
                size="small"
                onClick={() => history.push('/')}
                className="text-gray-600"
              >
                <IonIcon icon={arrowBackOutline} slot="start" />
                {t('common.backToHome')}
              </IonButton>
              <LanguageSwitcher size="small" mobileOnly={true} />
            </div>
            
            <div className="text-center mb-8">
              <h1 className="text-3xl font-heading font-bold text-gray-900 mb-2">
                {t('auth.login.title')}
              </h1>
              <p className="text-gray-600">
                {t('auth.login.subtitle')}
              </p>
            </div>

            <form onSubmit={handleLogin} className="space-y-6">
              <IonItem className="rounded-lg">
                <IonLabel position="stacked">{t('auth.login.emailLabel')}</IonLabel>
                <IonInput
                  type="email"
                  value={email}
                  onIonInput={(e) => setEmail(e.detail.value!)}
                  required
                  placeholder={t('auth.login.emailPlaceholder')}
                  autocomplete="email"
                />
              </IonItem>

              <IonItem className="rounded-lg">
                <IonLabel position="stacked">{t('auth.login.passwordLabel')}</IonLabel>
                <IonInput
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onIonInput={(e) => setPassword(e.detail.value!)}
                  required
                  placeholder={t('auth.login.passwordPlaceholder')}
                  autocomplete="current-password"
                />
                <IonButton
                  fill="clear"
                  slot="end"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <IonIcon icon={showPassword ? eyeOffOutline : eyeOutline} />
                </IonButton>
              </IonItem>

              <div className="text-right">
                <IonButton
                  fill="clear"
                  size="small"
                  onClick={() => {
                    setResetEmail(email)
                    setShowForgotPasswordAlert(true)
                  }}
                  className="text-primary-600"
                >
                  {t('auth.login.forgotPassword')}
                </IonButton>
              </div>

              <IonButton
                expand="block"
                type="submit"
                disabled={loading || !email.trim() || !password.trim()}
                className="mt-6"
              >
                {loading ? t('auth.login.signingIn') : t('auth.login.signInButton')}
              </IonButton>
            </form>

            <div className="mt-6 text-center">
              <IonText color="medium" className="text-sm">
                {t('auth.login.noAccount')}{' '}
                <span 
                  onClick={() => history.push('/register')}
                  className="text-primary-600 hover:text-primary-700 cursor-pointer font-medium underline"
                >
                  {t('auth.login.signUp')}
                </span>
              </IonText>
            </div>
          </div>
        </div>

        {/* Forgot Password Alert */}
        <IonAlert
          isOpen={showForgotPasswordAlert}
          onDidDismiss={() => setShowForgotPasswordAlert(false)}
          header={t('auth.resetPassword.forgotPassword.title')}
          message={t('auth.resetPassword.forgotPassword.message')}
          inputs={[
            {
              name: 'email',
              type: 'email',
              placeholder: t('auth.resetPassword.forgotPassword.emailPlaceholder'),
              value: resetEmail,
            },
          ]}
          buttons={[
            {
              text: t('common.cancel'),
              role: 'cancel',
            },
            {
              text: t('auth.resetPassword.forgotPassword.sendButton'),
              handler: (data) => {
                setResetEmail(data.email)
                handleForgotPassword()
                return false // Keep alert open until we handle the result
              },
            },
          ]}
        />

        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={4000}
          color={toastColor}
        />
      </IonContent>
    </IonPage>
  )
}

export default LoginPage 